import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Button,
  Group,
  ActionIcon,
  Box,
  List,
  ThemeIcon,
  ScrollArea,
  Affix,
  Transition,
  Alert,
  Badge,
  Card,
  Progress,
  Tabs,
  Timeline,
  Divider,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowUp,
  IconCheck,
  IconInfoCircle,
  IconShield,
  IconEye,
  IconLock,
  IconDatabase,
  IconCookie,
  IconMail,
  IconAlertTriangle,
  IconUserCheck,
  IconSettings,
  IconCloud,
  IconFingerprint,
  IconBell,
  IconShare,
  IconTrash,
  IconDownload,
  IconKey,
  IconWorld,
  IconBook,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWindowScroll } from "@mantine/hooks";
import dayjs from "dayjs";
import "./styles.scss";
import { AppDomain } from "../../Lib/data";

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scroll, scrollTo] = useWindowScroll();
  const [activeSection, setActiveSection] = useState("overview");
  const [readingProgress, setReadingProgress] = useState(0);

  const sections = [
    { id: "overview", title: "Privacy Overview", icon: IconInfoCircle },
    { id: "collection", title: "Data Collection", icon: IconDatabase },
    { id: "usage", title: "How We Use Data", icon: IconSettings },
    { id: "sharing", title: "Data Sharing", icon: IconShare },
    { id: "storage", title: "Data Storage", icon: IconCloud },
    { id: "security", title: "Security Measures", icon: IconShield },
    { id: "cookies", title: "Cookies & Tracking", icon: IconCookie },
    { id: "rights", title: "Your Rights", icon: IconUserCheck },
    { id: "children", title: "Children's Privacy", icon: IconLock },
    { id: "international", title: "International Transfers", icon: IconWorld },
    { id: "updates", title: "Policy Updates", icon: IconBell },
    { id: "contact", title: "Contact Us", icon: IconMail },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / documentHeight) * 100;
      setReadingProgress(Math.min(progress, 100));

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="privacy-container">
      {/* Reading Progress Bar */}
      <Box className="progress-bar">
        <Progress value={readingProgress} size="xs" color="violet" />
      </Box>

      {/* Hero Section */}
      <Box className="privacy-hero">
        <div className="hero-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`particle particle-${i + 1}`} />
          ))}
        </div>
        <Container size="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Group justify="space-between" align="center" mb="xl">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate(-1)}
                className="back-button"
              >
                Go Back
              </Button>
              <Badge variant="gradient" gradient={{ from: "violet", to: "blue" }} size="lg">
                GDPR Compliant • Last Updated: {dayjs().format("MMM DD, YYYY")}
              </Badge>
            </Group>

            <Stack align="center" gap="md" ta="center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, type: "spring" }}
              >
                <ThemeIcon
                  variant="gradient"
                  gradient={{ from: "violet", to: "blue" }}
                  size={100}
                  className="hero-icon"
                >
                  <IconShield size={60} />
                </ThemeIcon>
              </motion.div>
              <Title order={1} className="hero-title">
                Privacy Policy
              </Title>
              <Text size="xl" c="white" maw={700} className="hero-subtitle">
                Your privacy is our priority. Learn how we collect, use, and protect your personal
                information at Emdex Academy.
              </Text>
              <Group gap="md" mt="md">
                <Badge variant="light" color="white" size="lg">
                  <IconLock size={16} /> Encrypted Data
                </Badge>
                <Badge variant="light" color="white" size="lg">
                  <IconEye size={16} /> No Third-Party Tracking
                </Badge>
                <Badge variant="light" color="white" size="lg">
                  <IconFingerprint size={16} /> GDPR Compliant
                </Badge>
              </Group>
            </Stack>
          </motion.div>
        </Container>
      </Box>

      <Container size="lg" py="xl">
        <div className="privacy-content">
          {/* Navigation Sidebar */}
          <Paper className="privacy-nav" withBorder p="md" radius="md">
            <Group gap="sm" mb="md">
              <ThemeIcon variant="light" color="violet" size="sm">
                <IconEye size={14} />
              </ThemeIcon>
              <Title order={4}>Table of Contents</Title>
            </Group>
            <ScrollArea h={500}>
              <Stack gap="xs">
                {sections.map((section, index) => (
                  <motion.div
                    key={section.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Button
                      variant={activeSection === section.id ? "light" : "subtle"}
                      color="violet"
                      justify="flex-start"
                      leftSection={<section.icon size={16} />}
                      onClick={() => scrollToSection(section.id)}
                      size="sm"
                      fullWidth
                      className={`nav-button ${activeSection === section.id ? "active" : ""}`}
                    >
                      {section.title}
                    </Button>
                  </motion.div>
                ))}
              </Stack>
            </ScrollArea>

            {/* Quick Actions */}
            <Divider my="md" />
            <Stack gap="xs">
              <Button
                variant="outline"
                color="violet"
                size="xs"
                leftSection={<IconDownload size={14} />}
                fullWidth
              >
                Download PDF
              </Button>
              <Button
                variant="outline"
                color="violet"
                size="xs"
                leftSection={<IconKey size={14} />}
                fullWidth
              >
                Data Rights Request
              </Button>
            </Stack>
          </Paper>

          {/* Main Content */}
          <div className="privacy-main">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Privacy Overview */}
              <section id="overview" className="privacy-section">
                <Card withBorder p="xl" radius="md" mb="xl" className="intro-card">
                  <Group gap="md" mb="lg">
                    <ThemeIcon variant="gradient" gradient={{ from: "blue", to: "cyan" }} size="xl">
                      <IconInfoCircle size={24} />
                    </ThemeIcon>
                    <div>
                      <Title order={2}>Privacy Overview</Title>
                      <Text c="dimmed" size="sm">
                        Last updated: {dayjs().format("MMMM DD, YYYY")}
                      </Text>
                    </div>
                  </Group>

                  <Stack gap="lg">
                    <Text size="lg" fw={500} c="dark.7">
                      Welcome to Emdex Academy's Privacy Policy. We believe in transparency,
                      security, and your right to control your personal information.
                    </Text>

                    <div className="overview-grid">
                      <Card withBorder p="md" radius="md" className="overview-item">
                        <Group gap="sm" mb="xs">
                          <IconDatabase size={20} color="var(--mantine-color-blue-6)" />
                          <Text fw={600} size="sm">
                            Data Minimization
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          We only collect data necessary for providing our educational services.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md" className="overview-item">
                        <Group gap="sm" mb="xs">
                          <IconShield size={20} color="var(--mantine-color-green-6)" />
                          <Text fw={600} size="sm">
                            Security First
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Your data is protected with industry-standard encryption and security
                          measures.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md" className="overview-item">
                        <Group gap="sm" mb="xs">
                          <IconUserCheck size={20} color="var(--mantine-color-violet-6)" />
                          <Text fw={600} size="sm">
                            Your Rights
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Full control over your data with rights to access, update, or delete.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md" className="overview-item">
                        <Group gap="sm" mb="xs">
                          <IconEye size={20} color="var(--mantine-color-orange-6)" />
                          <Text fw={600} size="sm">
                            Transparency
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Clear, understandable information about how we handle your data.
                        </Text>
                      </Card>
                    </div>

                    <Alert
                      icon={<IconInfoCircle size="1rem" />}
                      title="Important Notice"
                      color="blue"
                      className="notice-alert"
                    >
                      This policy applies to all Emdex Academy services, including our website,
                      mobile applications, and educational platform. By using our services, you
                      consent to the practices described in this policy.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* Data Collection */}
              <section id="collection" className="privacy-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon
                      variant="gradient"
                      gradient={{ from: "violet", to: "purple" }}
                      size="xl"
                    >
                      <IconDatabase size={24} />
                    </ThemeIcon>
                    <Title order={2}>Information We Collect</Title>
                  </Group>

                  <Stack gap="lg">
                    <Tabs defaultValue="personal" className="data-tabs">
                      <Tabs.List>
                        <Tabs.Tab value="personal" leftSection={<IconUserCheck size={16} />}>
                          Personal Information
                        </Tabs.Tab>
                        <Tabs.Tab value="educational" leftSection={<IconBook size={16} />}>
                          Educational Data
                        </Tabs.Tab>
                        <Tabs.Tab value="technical" leftSection={<IconSettings size={16} />}>
                          Technical Data
                        </Tabs.Tab>
                      </Tabs.List>

                      <Tabs.Panel value="personal" pt="md">
                        <Stack gap="md">
                          <Text>
                            Information you provide when creating an account or using our services:
                          </Text>
                          <List
                            spacing="sm"
                            size="sm"
                            icon={
                              <ThemeIcon color="violet" size={20} radius="xl">
                                <IconCheck size="0.8rem" />
                              </ThemeIcon>
                            }
                          >
                            <List.Item>Full name and email address</List.Item>
                            <List.Item>Profile information and preferences</List.Item>
                            <List.Item>Payment and billing information</List.Item>
                            <List.Item>Communication preferences</List.Item>
                            <List.Item>Support and feedback messages</List.Item>
                          </List>
                        </Stack>
                      </Tabs.Panel>

                      <Tabs.Panel value="educational" pt="md">
                        <Stack gap="md">
                          <Text>Data related to your learning experience:</Text>
                          <List
                            spacing="sm"
                            size="sm"
                            icon={
                              <ThemeIcon color="blue" size={20} radius="xl">
                                <IconCheck size="0.8rem" />
                              </ThemeIcon>
                            }
                          >
                            <List.Item>Course enrollment and progress data</List.Item>
                            <List.Item>Assessment scores and completion rates</List.Item>
                            <List.Item>Discussion forum posts and interactions</List.Item>
                            <List.Item>Assignment submissions and project work</List.Item>
                            <List.Item>Learning preferences and recommendations</List.Item>
                          </List>
                        </Stack>
                      </Tabs.Panel>

                      <Tabs.Panel value="technical" pt="md">
                        <Stack gap="md">
                          <Text>Automatically collected technical information:</Text>
                          <List
                            spacing="sm"
                            size="sm"
                            icon={
                              <ThemeIcon color="green" size={20} radius="xl">
                                <IconCheck size="0.8rem" />
                              </ThemeIcon>
                            }
                          >
                            <List.Item>Device information and browser type</List.Item>
                            <List.Item>IP address and geographic location</List.Item>
                            <List.Item>Usage patterns and session data</List.Item>
                            <List.Item>Performance and error logs</List.Item>
                            <List.Item>Cookie and tracking preferences</List.Item>
                          </List>
                        </Stack>
                      </Tabs.Panel>
                    </Tabs>

                    <Alert icon={<IconLock size="1rem" />} title="Data Security" color="green">
                      All personal information is encrypted during transmission and storage. We
                      never collect sensitive information unless absolutely necessary for our
                      services.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* How We Use Data */}
              <section id="usage" className="privacy-section">
                <Card withBorder p="xl" radius="md" mb="xl">
                  <Group gap="md" mb="lg">
                    <ThemeIcon
                      variant="gradient"
                      gradient={{ from: "green", to: "teal" }}
                      size="xl"
                    >
                      <IconSettings size={24} />
                    </ThemeIcon>
                    <Title order={2}>How We Use Your Information</Title>
                  </Group>

                  <Stack gap="lg">
                    <div className="usage-timeline">
                      <Timeline active={-1} bulletSize={24} lineWidth={2} color="violet">
                        <Timeline.Item
                          bullet={<IconUserCheck size={12} />}
                          title="Account Management"
                        >
                          <Text size="sm" c="dimmed" mb="xs">
                            Creating and maintaining your account, authentication, and profile
                            management.
                          </Text>
                        </Timeline.Item>

                        <Timeline.Item bullet={<IconBook size={12} />} title="Educational Services">
                          <Text size="sm" c="dimmed" mb="xs">
                            Delivering courses, tracking progress, providing personalized learning
                            experiences.
                          </Text>
                        </Timeline.Item>

                        <Timeline.Item bullet={<IconMail size={12} />} title="Communications">
                          <Text size="sm" c="dimmed" mb="xs">
                            Sending course updates, announcements, and important service
                            information.
                          </Text>
                        </Timeline.Item>

                        <Timeline.Item
                          bullet={<IconShield size={12} />}
                          title="Security & Fraud Prevention"
                        >
                          <Text size="sm" c="dimmed" mb="xs">
                            Protecting your account, preventing unauthorized access, and maintaining
                            platform security.
                          </Text>
                        </Timeline.Item>

                        <Timeline.Item
                          bullet={<IconSettings size={12} />}
                          title="Service Improvement"
                        >
                          <Text size="sm" c="dimmed" mb="xs">
                            Analyzing usage patterns to improve our platform and develop new
                            features.
                          </Text>
                        </Timeline.Item>
                      </Timeline>
                    </div>

                    <div className="purpose-grid">
                      <Card withBorder p="md" radius="md" className="purpose-card">
                        <Group gap="sm" mb="sm">
                          <IconFingerprint size={20} color="var(--mantine-color-violet-6)" />
                          <Text fw={600} size="sm">
                            Personalization
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Customizing your learning experience based on preferences and progress.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md" className="purpose-card">
                        <Group gap="sm" mb="sm">
                          <IconBell size={20} color="var(--mantine-color-blue-6)" />
                          <Text fw={600} size="sm">
                            Notifications
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Sending relevant updates about courses, deadlines, and platform news.
                        </Text>
                      </Card>

                      <Card withBorder p="md" radius="md" className="purpose-card">
                        <Group gap="sm" mb="sm">
                          <IconDatabase size={20} color="var(--mantine-color-green-6)" />
                          <Text fw={600} size="sm">
                            Analytics
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Understanding platform usage to improve performance and user experience.
                        </Text>
                      </Card>
                    </div>
                  </Stack>
                </Card>
              </section>

              {/* Your Rights */}
              <section id="rights" className="privacy-section">
                <Card withBorder p="xl" radius="md" mb="xl" className="rights-card">
                  <Group gap="md" mb="lg">
                    <ThemeIcon
                      variant="gradient"
                      gradient={{ from: "orange", to: "red" }}
                      size="xl"
                    >
                      <IconUserCheck size={24} />
                    </ThemeIcon>
                    <Title order={2}>Your Privacy Rights</Title>
                  </Group>

                  <Stack gap="lg">
                    <Text size="lg" fw={500}>
                      Under GDPR and other privacy laws, you have comprehensive rights regarding
                      your personal data:
                    </Text>

                    <div className="rights-grid">
                      <Card withBorder p="lg" radius="md" className="right-item">
                        <Group gap="md" mb="md">
                          <ThemeIcon variant="light" color="blue" size="lg">
                            <IconEye size={20} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600}>Right to Access</Text>
                            <Text size="sm" c="dimmed">
                              Request a copy of your personal data
                            </Text>
                          </div>
                        </Group>
                        <Text size="sm">
                          You can request access to all personal information we hold about you,
                          including how it's being used and who it's shared with.
                        </Text>
                      </Card>

                      <Card withBorder p="lg" radius="md" className="right-item">
                        <Group gap="md" mb="md">
                          <ThemeIcon variant="light" color="green" size="lg">
                            <IconSettings size={20} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600}>Right to Rectification</Text>
                            <Text size="sm" c="dimmed">
                              Correct inaccurate information
                            </Text>
                          </div>
                        </Group>
                        <Text size="sm">
                          You can request corrections to any inaccurate or incomplete personal
                          information we hold about you.
                        </Text>
                      </Card>

                      <Card withBorder p="lg" radius="md" className="right-item">
                        <Group gap="md" mb="md">
                          <ThemeIcon variant="light" color="red" size="lg">
                            <IconTrash size={20} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600}>Right to Erasure</Text>
                            <Text size="sm" c="dimmed">
                              Delete your personal data
                            </Text>
                          </div>
                        </Group>
                        <Text size="sm">
                          Request deletion of your personal data when it's no longer necessary for
                          the purposes it was collected.
                        </Text>
                      </Card>

                      <Card withBorder p="lg" radius="md" className="right-item">
                        <Group gap="md" mb="md">
                          <ThemeIcon variant="light" color="violet" size="lg">
                            <IconDownload size={20} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600}>Right to Portability</Text>
                            <Text size="sm" c="dimmed">
                              Export your data
                            </Text>
                          </div>
                        </Group>
                        <Text size="sm">
                          Receive your personal data in a structured, commonly used format that you
                          can transfer to another service.
                        </Text>
                      </Card>
                    </div>

                    <Alert
                      icon={<IconMail size="1rem" />}
                      title="Exercise Your Rights"
                      color="violet"
                    >
                      To exercise any of these rights, contact us at info@{AppDomain}. We'll respond
                      within 30 days and verify your identity before processing requests.
                    </Alert>
                  </Stack>
                </Card>
              </section>

              {/* Contact Information */}
              <section id="contact" className="privacy-section">
                <Card withBorder p="xl" radius="md" mb="xl" className="contact-card">
                  <Group gap="md" mb="lg">
                    <ThemeIcon
                      variant="gradient"
                      gradient={{ from: "blue", to: "violet" }}
                      size="xl"
                    >
                      <IconMail size={24} />
                    </ThemeIcon>
                    <Title order={2}>Privacy Contact</Title>
                  </Group>

                  <Stack gap="lg">
                    <Text>
                      Have questions about this privacy policy or how we handle your data? We're
                      here to help and ensure your privacy rights are protected.
                    </Text>

                    <div className="contact-options">
                      <Card withBorder p="lg" radius="md" className="contact-option">
                        <Group gap="md">
                          <ThemeIcon variant="light" color="blue" size="xl">
                            <IconMail size={24} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600} size="lg">
                              Data Protection Officer
                            </Text>
                            <Text size="sm" c="dimmed" mb="xs">
                              For privacy-related inquiries and requests
                            </Text>
                            <Text fw={500} c="blue">
                              info@{AppDomain}
                            </Text>
                          </div>
                        </Group>
                      </Card>

                      <Card withBorder p="lg" radius="md" className="contact-option">
                        <Group gap="md">
                          <ThemeIcon variant="light" color="green" size="xl">
                            <IconUserCheck size={24} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600} size="lg">
                              Data Rights Requests
                            </Text>
                            <Text size="sm" c="dimmed" mb="xs">
                              Access, correct, or delete your data
                            </Text>
                            <Text fw={500} c="green">
                              info@{AppDomain}
                            </Text>
                          </div>
                        </Group>
                      </Card>
                    </div>

                    <Alert
                      icon={<IconAlertTriangle size="1rem" />}
                      title="Response Time"
                      color="orange"
                    >
                      We aim to respond to all privacy inquiries within 30 days. For urgent security
                      concerns, contact us immediately at info@{AppDomain}.
                    </Alert>

                    <Text size="sm" c="dimmed" ta="center">
                      This Privacy Policy is effective as of {dayjs().format("MMMM DD, YYYY")}
                      and was last updated on {dayjs().format("MMMM DD, YYYY")}.
                    </Text>
                  </Stack>
                </Card>
              </section>
            </motion.div>
          </div>
        </div>
      </Container>

      {/* Scroll to Top Button */}
      <Affix position={{ bottom: 20, right: 20 }}>
        <Transition transition="slide-up" mounted={scroll.y > 0}>
          {(transitionStyles) => (
            <ActionIcon
              style={transitionStyles}
              onClick={() => scrollTo({ y: 0 })}
              size="lg"
              variant="gradient"
              gradient={{ from: "violet", to: "blue" }}
              radius="xl"
              className="scroll-top"
            >
              <IconArrowUp size="1.2rem" />
            </ActionIcon>
          )}
        </Transition>
      </Affix>
    </div>
  );
};

export default PrivacyPolicy;
